import { notification } from "antd";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { ROUTES } from "@/constants";
import { useAppSelector } from "@/hooks/redux";
import {
    setLatency,
    setShowOnlinePopup,
    updateOfflineStatus,
} from "@/store/slices/offline/offline.slice";

export const NETWORK_ERROR_CODES = [
    "ECONNABORTED",
    "NETWORK_ERROR",
    "ERR_INTERNET_DISCONNECTED",
    "ERR_NETWORK",
    "Failed to fetch",
    "TypeError",
];

const OfflineModeHandler: React.FC = () => {
    const dispatch = useDispatch();
    const { manualOfflineMode } = useAppSelector((state) => state.offline);

    const wasOfflineRef = useRef(false);
    const slowCountRef = useRef(0);
    const successCountRef = useRef(0);

    const checkPingLatency = useCallback(async () => {
        try {
            if (manualOfflineMode) {
                dispatch(setLatency("offline"));
                return;
            }
            const startTime = Date.now();
            await fetch("/ping", { method: "HEAD", cache: "no-store" });
            const latency = Date.now() - startTime;

            dispatch(setLatency(latency));
            if (latency > 3000) {
                slowCountRef.current += 1;
                successCountRef.current = 0;

                if (slowCountRef.current >= 3 && !wasOfflineRef.current) {
                    dispatch(updateOfflineStatus(true));
                    wasOfflineRef.current = true;
                }
            } else {
                successCountRef.current += 1;
                slowCountRef.current = 0;

                if (successCountRef.current >= 3 && wasOfflineRef.current) {
                    wasOfflineRef.current = false;
                    const workPlanPath = ROUTES.WorkPlan;

                    const { pathname } = location;

                    if (
                        !pathname.startsWith(workPlanPath) ||
                        pathname === workPlanPath ||
                        pathname === `${workPlanPath}/`
                    ) {
                        dispatch(updateOfflineStatus(false));

                        notification.success({
                            message: "З'єднання відновлено",
                        });
                        return;
                    }

                    dispatch(setShowOnlinePopup(true));
                }
            }
        } catch (error) {
            const code =
                error?.code || error?.name || error?.message || "UNKNOWN_ERROR";
            if (NETWORK_ERROR_CODES.includes(code)) {
                dispatch(setLatency("offline"));
            }

            slowCountRef.current += 1;
            successCountRef.current = 0;

            if (slowCountRef.current >= 3 && !wasOfflineRef.current) {
                dispatch(updateOfflineStatus(true));
                wasOfflineRef.current = true;
            }
        }
    }, [dispatch, manualOfflineMode]);

    useEffect(() => {
        const checkNetworkQuality = () => {
            checkPingLatency();
        };

        checkNetworkQuality();

        if (navigator.connection) {
            navigator.connection.addEventListener(
                "change",
                checkNetworkQuality,
            );
        }

        const intervalId = setInterval(checkPingLatency, 10000);
        if (manualOfflineMode) {
            clearInterval(intervalId);
        }

        return () => {
            if (navigator.connection) {
                navigator.connection.removeEventListener(
                    "change",
                    checkNetworkQuality,
                );
            }
            clearInterval(intervalId);
        };
    }, [checkPingLatency, manualOfflineMode]);

    return null;
};

export default OfflineModeHandler;
